import React, { useState, useEffect } from 'react';
import MunicipalitySearch from '@ta-interaktiv/react-municipality-search';
import { scaleLinear, geoMercator, geoPath, select, selectAll } from 'd3';
import { feature, mesh } from 'topojson-client';

var ChoroplethMap = function (_a) {
    var municipalityData = _a.municipalityData, propertyNameToCompare = _a.propertyNameToCompare, minValue = _a.minValue, maxValue = _a.maxValue, year = _a.year, activeMunicipalityId = _a.activeMunicipalityId, newActiveMunicipalityIdHandler = _a.newActiveMunicipalityIdHandler, hoverHandler = _a.hoverHandler, colorPalette = _a.colorPalette, activeStrokeWidth = _a.activeStrokeWidth, activeStrokeColor = _a.activeStrokeColor, municipalityStrokeColor = _a.municipalityStrokeColor, municipalityStrokeWidth = _a.municipalityStrokeWidth, hoverStrokeWidth = _a.hoverStrokeWidth, hoverStrokeColor = _a.hoverStrokeColor, lakeFillColor = _a.lakeFillColor, lakeStrokeColor = _a.lakeStrokeColor, lakeStrokeWidth = _a.lakeStrokeWidth, nationalBorderColor = _a.nationalBorderColor, nationalBorderWidth = _a.nationalBorderWidth, cantonStrokeColor = _a.cantonStrokeColor, cantonStrokeWidth = _a.cantonStrokeWidth;
    var _b = useState(), chTopoJSON = _b[0], setChTopoJSON = _b[1];
    var _c = useState(), path = _c[0], setPath = _c[1];
    var _d = useState(), geoJsonMuni = _d[0], setGeoJsonMuni = _d[1];
    var tooltipDistanceToCursor = 10;
    var color = scaleLinear()
        .domain([minValue, (minValue + maxValue) / 2, maxValue])
        .range(colorPalette || ['green', 'lightgrey', 'red']);
    function getValueToCompare(bfsIdToFind) {
        var foundInMunicipaltyData = municipalityData.find(function (muni) { return +muni.bfsId === +bfsIdToFind; });
        return foundInMunicipaltyData
            ? foundInMunicipaltyData[propertyNameToCompare]
            : undefined;
    }
    function repositionToolTip(mouseX, mouseY) {
        var toolTipElement = select('#tooltip').node();
        var widthOfTooltip = toolTipElement === null || toolTipElement === void 0 ? void 0 : toolTipElement.clientWidth;
        var windowWidth = document.documentElement.clientWidth;
        var toolTipShouldBeLeft = widthOfTooltip + mouseX + tooltipDistanceToCursor > windowWidth;
        if (!toolTipShouldBeLeft) {
            select('#tooltip')
                .style('left', mouseX + tooltipDistanceToCursor + 'px')
                .style('top', mouseY + tooltipDistanceToCursor + 'px');
        }
        else {
            select('#tooltip')
                .style('left', mouseX - widthOfTooltip - tooltipDistanceToCursor + 'px')
                .style('top', mouseY + tooltipDistanceToCursor + 'px');
        }
    }
    function addHoverInteractions() {
        var mapElement = select('#swiss-choropleth-map').node();
        if ((mapElement === null || mapElement === void 0 ? void 0 : mapElement.clientWidth) > 600) {
            selectAll('.municipality-highlight')
                .on('mouseover', function (event) {
                var _this = this;
                select('#tooltip').style('opacity', 1);
                repositionToolTip(event.pageX, event.pageY);
                hoverHandler(municipalityData.find(function (muni) { return +muni.bfsId === +select(_this).attr('bfsId'); }));
                select(this)
                    .transition()
                    .duration(20)
                    .style('stroke-width', hoverStrokeWidth)
                    .style('stroke', hoverStrokeColor);
            })
                .on('mouseout', function () {
                select('#tooltip').style('opacity', 0);
                if (!(+select(this).attr('bfsId') === activeMunicipalityId)) {
                    select(this)
                        .transition()
                        .duration(300)
                        .style('stroke', 'transparent')
                        .style('stroke-width', 'transparent');
                }
            })
                .on('mousemove', function (event) {
                repositionToolTip(event.pageX, event.pageY);
            });
        }
    }
    function highlightActiveMunicipality() {
        if (activeMunicipalityId) {
            select('#bfsId-' + activeMunicipalityId)
                .transition()
                .duration(20)
                .style('stroke-width', activeStrokeWidth)
                .style('stroke', activeStrokeColor);
            addHoverInteractions();
        }
    }
    useEffect(function () {
        fetch('https://unpkg.com/swiss-maps/' + year + '/ch-combined.json').then(function (res) {
            return res.json().then(function (parsed) {
                setGeoJsonMuni(feature(parsed, parsed.objects.municipalities));
                setChTopoJSON(parsed);
                var projection = geoMercator()
                    .rotate([0, 0])
                    .center([8.23, 46.885])
                    .scale(11980)
                    .translate([975 / 2, 610 / 2])
                    .precision(0.1);
                setPath(function () { return geoPath(projection); });
            });
        });
    }, []);
    useEffect(function () {
        if (geoJsonMuni && path) {
            select('#highlight-layer')
                .selectAll('path')
                .data(geoJsonMuni.features)
                .enter()
                .append('path')
                .attr('d', function (feature) { return path(feature); })
                .attr('class', 'municipality-highlight')
                .attr('stroke', 'transparent')
                .attr('strokeLinejoin', 'round')
                .attr('strokeLinecap', 'round')
                .attr('fill', 'transparent')
                .attr('id', function (feature) { return 'bfsId-' + feature.id; })
                .attr('bfsId', function (feature) { return feature.id; })
                .on('click', function (event, feature) {
                if (event.pointerType !== 'touch') {
                    newActiveMunicipalityIdHandler(feature.id);
                }
            });
            if (activeMunicipalityId) {
                highlightActiveMunicipality();
            }
            else {
                addHoverInteractions();
            }
            select('#municipality-layer')
                .selectAll('path')
                .data(geoJsonMuni.features)
                .enter()
                .append('path')
                .attr('d', function (feature) { return path(feature); })
                .attr('class', 'municipality')
                .attr('stroke', municipalityStrokeColor)
                .attr('stroke-width', municipalityStrokeWidth)
                .attr('fill', function (feature) {
                var valueToCompare = getValueToCompare(feature.id);
                return valueToCompare !== undefined
                    ? color(valueToCompare)
                    : municipalityStrokeColor;
            });
        }
    }, [geoJsonMuni, path]);
    var colorMunicipalities = function () {
        select('#municipality-layer')
            .selectAll('path')
            .attr('fill', function (feature) {
            var valueToCompare = getValueToCompare(feature.id);
            return valueToCompare !== undefined
                ? color(valueToCompare)
                : municipalityStrokeColor;
        });
    };
    useEffect(function () {
        selectAll('.municipality-highlight')
            .transition()
            .duration(300)
            .style('stroke', 'transparent');
        highlightActiveMunicipality();
    }, [activeMunicipalityId]);
    useEffect(function () {
        colorMunicipalities();
        addHoverInteractions();
    }, [municipalityData]);
    return (React.createElement("div", null,
        React.createElement("div", { id: 'swiss-choropleth-map' }, chTopoJSON && path && municipalityData && (React.createElement("svg", { viewBox: '0 0 975 680' },
            React.createElement("g", { id: 'municipality-layer' }),
            React.createElement("g", { fill: 'none', stroke: '#000', strokeLinejoin: 'round', strokeLinecap: 'round' },
                React.createElement("path", { strokeWidth: cantonStrokeWidth, stroke: cantonStrokeColor, d: path(mesh(chTopoJSON, chTopoJSON.objects.cantons, function (a, b) { return a !== b; })) }),
                React.createElement("path", { strokeWidth: nationalBorderWidth, id: 'country-border', stroke: nationalBorderColor, d: path(feature(chTopoJSON, chTopoJSON.objects.country)) }),
                React.createElement("path", { fill: lakeFillColor, stroke: lakeStrokeColor, strokeWidth: lakeStrokeWidth, d: path(feature(chTopoJSON, chTopoJSON.objects.lakes)) })),
            React.createElement("g", { id: 'highlight-layer' }))))));
};
var ChoroplethMap$1 = React.memo(ChoroplethMap, function (prevProps, nextProps) {
    return prevProps.municipalityData === nextProps.municipalityData &&
        prevProps.activeMunicipalityId === nextProps.activeMunicipalityId &&
        prevProps.minValue === nextProps.minValue &&
        prevProps.maxValue === nextProps.maxValue &&
        prevProps.year === nextProps.year;
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') { return; }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "#tooltip {\n  pointer-events: none;\n  position: absolute;\n  opacity: 0;\n  -webkit-transition: opacity 0.3s ease-in-out;\n  transition: opacity 0.3s ease-in-out; }\n";
styleInject(css_248z);

var SwissMap = function (_a) {
    var municipalityData = _a.municipalityData, propertyNameToCompare = _a.propertyNameToCompare, _b = _a.hideMunicipalitySearch, hideMunicipalitySearch = _b === void 0 ? false : _b, minValue = _a.minValue, maxValue = _a.maxValue, municipalitySearchYear = _a.municipalitySearchYear, topoJsonYear = _a.topoJsonYear, newActiveMunicipalityIdHandler = _a.newActiveMunicipalityIdHandler, _c = _a.activeMunicipalityId, activeMunicipalityId = _c === void 0 ? 0 : _c, hoverHandler = _a.hoverHandler, ToolTip = _a.ToolTip, colorPalette = _a.colorPalette, _d = _a.activeStrokeWidth, activeStrokeWidth = _d === void 0 ? '2' : _d, _e = _a.activeStrokeColor, activeStrokeColor = _e === void 0 ? '#ffff' : _e, _f = _a.hoverStrokeWidth, hoverStrokeWidth = _f === void 0 ? '2' : _f, _g = _a.hoverStrokeColor, hoverStrokeColor = _g === void 0 ? '#ffff' : _g, _h = _a.municipalityStrokeColor, municipalityStrokeColor = _h === void 0 ? '#aaa' : _h, _j = _a.municipalityStrokeWidth, municipalityStrokeWidth = _j === void 0 ? '0.5' : _j, _k = _a.lakeFillColor, lakeFillColor = _k === void 0 ? 'rgb(239, 236, 224)' : _k, _l = _a.lakeStrokeColor, lakeStrokeColor = _l === void 0 ? 'none' : _l, _m = _a.lakeStrokeWidth, lakeStrokeWidth = _m === void 0 ? '0' : _m, _o = _a.nationalBorderColor, nationalBorderColor = _o === void 0 ? 'none' : _o, _p = _a.nationalBorderWidth, nationalBorderWidth = _p === void 0 ? '0' : _p, _q = _a.cantonStrokeColor, cantonStrokeColor = _q === void 0 ? 'rgba(0,0,0,0.4)' : _q, _r = _a.cantonStrokeWidth, cantonStrokeWidth = _r === void 0 ? '1' : _r;
    var _s = useState(), toolTipData = _s[0], setToolTipData = _s[1];
    return (React.createElement("div", null,
        !hideMunicipalitySearch && municipalitySearchYear && (React.createElement(MunicipalitySearch, { municipalityData: municipalitySearchYear, locale: 'de', dedupe: true, hideTooltip: true, resetOnSelect: true, placeholder: 'Gemeindesuche', onSelectionHandler: function (selectedMunicipality) {
                if (newActiveMunicipalityIdHandler) {
                    newActiveMunicipalityIdHandler(selectedMunicipality.GDENR);
                }
            } })),
        React.createElement(ChoroplethMap$1, { municipalityData: municipalityData, propertyNameToCompare: propertyNameToCompare, minValue: minValue, maxValue: maxValue, year: topoJsonYear, activeMunicipalityId: activeMunicipalityId, newActiveMunicipalityIdHandler: function (newId) {
                if (newActiveMunicipalityIdHandler) {
                    newActiveMunicipalityIdHandler(newId);
                }
            }, hoverHandler: function (newData) {
                if (hoverHandler) {
                    hoverHandler(newData);
                }
                if (ToolTip) {
                    setToolTipData(newData);
                }
            }, colorPalette: colorPalette, activeStrokeWidth: activeStrokeWidth, activeStrokeColor: activeStrokeColor, hoverStrokeWidth: hoverStrokeWidth, hoverStrokeColor: hoverStrokeColor, municipalityStrokeColor: municipalityStrokeColor, municipalityStrokeWidth: municipalityStrokeWidth, lakeFillColor: lakeFillColor, lakeStrokeColor: lakeStrokeColor, lakeStrokeWidth: lakeStrokeWidth, nationalBorderColor: nationalBorderColor, nationalBorderWidth: nationalBorderWidth, cantonStrokeColor: cantonStrokeColor, cantonStrokeWidth: cantonStrokeWidth }),
        ToolTip && (React.createElement("div", { id: 'tooltip' },
            React.createElement(ToolTip, { activeData: toolTipData })))));
};

export { SwissMap };
//# sourceMappingURL=index.m.js.map
